import { useI18n } from '../../i18n';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, PageContainer, ActionButton, ExportButton } from '../../components/shared';
import { PlusIcon, EditIcon, TrashIcon } from '../../components/icons';
import { useCustomers, useCustomersStats, useCreateCustomer, useUpdateCustomer, useDeleteCustomer, useExport, useLoyaltyAccounts } from '../../hooks';
import { useDebounce } from '../../hooks/useDebounce';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { Pagination } from '../../components/shared';
import { TierBadge, type CustomerTier } from './components/TierBadge';
import type { UpdateCustomerRequest } from '@conai/shared-types';

// Bug #83: bỏ khái niệm status Active/Inactive — Customer không có field status
// riêng (chỉ có is_registered), filter/cột Inactive gây hiểu nhầm dữ liệu.
interface CustomerDisplay {
  id: string;
  code: string;
  fullName: string;
  email: string;
  /**
   * Bug #104: số điện thoại THẬT — chuỗi rỗng khi khách chưa có, KHÔNG phải
   * '-'. Trước đây field này mang sẵn placeholder hiển thị, mà modal edit lại
   * nạp thẳng nó vào form, nên bấm Lưu là gửi `phone: '-'` lên server.
   */
  phone: string;
  marketingConsent: boolean;
  /**
   * Hồ sơ này đã gắn tài khoản đăng nhập chưa (sheet 2 mục #65).
   *
   * Đọc THẲNG `Customer.user_id` — serializer LIST của `/customers/` đã trả
   * field này từ bản deploy 04/09/2026.
   *
   * KHÔNG suy từ `is_registered`: hiện BE suy nó từ `user_id != null` nên hai
   * giá trị trùng nhau, nhưng đó là chi tiết cài đặt của BE chứ không phải
   * hợp đồng — đổi cách suy là màn này hiện sai mà không ai biết.
   */
  tier: CustomerTier;
  isVip?: boolean;
  /**
   * Bug #95: ghi chú nội bộ về khách.
   *
   * Trước đây `CustomerDisplay` không mang trường này, nên dù API list có trả
   * `notes` thì nó cũng bị cắt ngay ở bước map — và `openEdit` lại gán cứng
   * `notes: ''`. Kết quả: gõ ghi chú, bấm Lưu, mở lại thì trắng trơn.
   */
  notes: string;
}

type ModalMode = 'add' | 'edit' | null;

interface CustomerFormData {
  fullName: string;
  phone: string;
  email: string;
  marketingConsent: boolean;
  notes: string;
}

const emptyForm: CustomerFormData = {
  fullName: '', phone: '', email: '', marketingConsent: false, notes: '',
};

/**
 * Lỗi của form: mỗi ô nhập một key, cộng thêm `contact` cho rule cấp FORM.
 *
 * `contact` không thuộc về riêng ô nào — nó phát biểu về CẶP phone/email, nên
 * không thể nhét vào `keyof CustomerFormData` mà vẫn đọc xuôi được.
 */
type CustomerFormErrors = Partial<Record<keyof CustomerFormData, string>> & {
  contact?: string;
};

/**
 * Ràng buộc DB của bảng Customer: phải còn ít nhất MỘT trong `phone` / `email`.
 *
 * Kiểm ở FE để người dùng biết ngay tại chỗ, thay vì bấm Lưu rồi nhận 400 kèm
 * câu tiếng Anh của server ("At least phone or email must be provided.").
 *
 * Tính trên giá trị SAU KHI áp thay đổi của form, không phải trên field đang
 * gõ: form edit được nạp sẵn dữ liệu hiện có của khách, nên `formData` chính là
 * bản khách sẽ thành sau khi lưu — kể cả những field người dùng không đụng tới
 * và do đó không nằm trong payload.
 *
 * Khoảng trắng không phải là dữ liệu liên lạc, nên trim trước khi xét.
 */
function hasContactInfo(form: Pick<CustomerFormData, 'phone' | 'email'>): boolean {
  return Boolean(form.phone.trim() || form.email.trim());
}

/**
 * Bug #104: đọc lý do thật từ lỗi API.
 *
 * Trước đây mọi thất bại đều hiện đúng một câu "Failed to save customer", nên
 * người dùng không có cách nào biết field nào sai — mà server thì đã nói rõ
 * (`details`, `errors`, hoặc `error.message`). Giữ nguyên câu chung chỉ khi
 * thật sự không moi được gì.
 */
function extractApiErrorMessage(err: unknown, t: (key: string) => string): string {
  const fallback = t('failedToSaveCustomer');
  if (!err || typeof err !== 'object') return fallback;

  const e = err as {
    message?: string;
    details?: Array<{ field: string; message: string }>;
    originalError?: {
      response?: {
        data?: {
          error?: { message?: string };
          message?: string;
          errors?: Record<string, string[] | string>;
        };
      };
    };
  };

  if (Array.isArray(e.details) && e.details.length > 0) {
    return e.details.map((d) => `${d.field}: ${d.message}`).join('\n');
  }

  const raw = e.originalError?.response?.data;
  if (raw?.errors) {
    const parts = Object.entries(raw.errors).map(
      ([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`
    );
    if (parts.length > 0) return parts.join('\n');
  }
  if (raw?.error?.message) return raw.error.message;
  if (raw?.message) return raw.message;

  // 'Request failed with status code 400' không nói lên điều gì — bỏ.
  if (e.message && !e.message.startsWith('Request failed')) return e.message;
  return fallback;
}

function StatCard({ label, value, change, changeLabel, icon }: {
  label: string; value: string | number;
  change?: string; changeLabel?: string; icon: React.ReactNode;
}) {
  const isPositive = change && !change.startsWith('-');
  return (
    <div className="flex items-center justify-between rounded-xl bg-white px-6 py-5" style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
      <div>
        <p className="mb-1 text-xs font-medium text-slate-500">{label}</p>
        <div className="flex items-baseline gap-2">
          <p className="font-sans text-[32px] font-bold leading-none text-slate-900">{value}</p>
          {change && (
            <span className="text-xs font-medium">
              <span className={isPositive ? 'text-[#22C55E]' : 'text-[#EF4444]'}>{change}</span>
              {changeLabel && <span className="text-slate-900"> {changeLabel}</span>}
            </span>
          )}
        </div>
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50">{icon}</div>
    </div>
  );
}

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ChevronIcon = () => (
  <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function CustomersPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchInput, 500);
  const [tierFilter, setTierFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  /** Khách đang mở modal chọn tài khoản để liên kết (mục #65). */
  /** Khách đang chờ xác nhận GỠ liên kết. */
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  /**
   * Lý do không xoá được, hiện NGAY TRONG hộp thoại xoá.
   *
   * Bản cũ nuốt lỗi: `catch` chỉ đóng hộp thoại, không nói gì. Nhân viên bấm
   * Xoá, hộp thoại biến mất, khách vẫn nằm đó — trông y hệt lỗi giao diện.
   * Từ 23/09/2026 BE trả 409 `customer_linked_to_account` khi hồ sơ đang gắn
   * tài khoản còn hoạt động, kèm câu chỉ rõ phải làm gì, nên phải hiện ra.
   */
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Khoá scroll nền khi modal thêm/sửa hoặc popup xác nhận xoá đang mở.
  useLockBodyScroll(modalMode !== null || deleteConfirmId !== null);
  const [formData, setFormData] = useState<CustomerFormData>(emptyForm);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<CustomerFormErrors>({});
  /**
   * Bug #104: giá trị của khách ĐANG mở, chụp lại đúng lúc mở modal.
   *
   * Dùng để so ra phần người dùng thật sự sửa — payload chỉ mang đúng những
   * field đó. Không có mốc so sánh thì mỗi lần lưu đều gửi lại toàn bộ form,
   * kể cả field chỉ tồn tại trên giao diện, và một khách thiếu dữ liệu là đủ
   * để server từ chối cả request.
   */
  const initialFormRef = useRef<CustomerFormData>(emptyForm);
  const pageSize = 10;

  const lastSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    setSearchInput(searchParams.get('search') || '');
    setCurrentPage(Number(searchParams.get('page') || '1'));
  }, [searchParams]);

  const lastTierRef = useRef(tierFilter);

  useEffect(() => {
    if (lastTierRef.current !== tierFilter) {
      lastTierRef.current = tierFilter;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('page', '1');
        return next;
      });
      setCurrentPage(1);
    }
  }, [tierFilter, setSearchParams]);

  useEffect(() => {
    if (lastSearchRef.current !== debouncedSearch) {
      lastSearchRef.current = debouncedSearch;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set('search', debouncedSearch);
        } else {
          next.delete('search');
        }
        next.set('page', '1');
        return next;
      });
    }
  }, [debouncedSearch, setSearchParams]);

  const { data: customersData, isLoading } = useCustomers({ 
    page: 1, 
    page_size: 100,
    search: debouncedSearch || undefined,
  });
  const { data: statsData } = useCustomersStats();
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();

  const { data: loyaltyAccounts } = useLoyaltyAccounts({ page_size: 100 });

  const loyaltyMap = useMemo(() => {
    const map = new Map<string, string>();
    if (loyaltyAccounts?.results) {
      for (const acc of loyaltyAccounts.results) {
        if (acc.customer_id) {
          map.set(acc.customer_id, acc.tier || 'NONE');
        }
      }
    }
    return map;
  }, [loyaltyAccounts]);

  const customers = useMemo<CustomerDisplay[]>(
    () => (customersData?.results ?? []).map((c, i) => {
      const activeTier = loyaltyMap.get(c.id) || (c as any).tier || 'NONE';
      return {
        id: c.id,
        code: `CT${String(i + 1).padStart(3, '0')}`,
        fullName: c.full_name,
        email: (c as any).email ?? '',
        // Bug #104: placeholder '-' chỉ được sinh ra ở chỗ VẼ (xem cột Phone),
        // không nhét vào dữ liệu — nếu không thì form edit gửi '-' đi thật.
        phone: c.phone ?? '',
        marketingConsent: c.marketing_consent ?? false,
        tier: (activeTier as CustomerTier) ?? 'NONE',
        isVip: ['ELITE', 'PLATINUM', 'GOLD'].includes(activeTier),
        // Bug #95: đọc cả hai tên field — serializer có thể dùng `notes` hoặc `note`.
        notes: String((c as any).notes ?? (c as any).note ?? ''),
      };
    }),
    [customersData, loyaltyMap]
  );

  const filtered = useMemo(() => customers.filter((c) => {
    const matchSearch = !debouncedSearch || c.fullName.toLowerCase().includes(debouncedSearch.toLowerCase()) || c.code.toLowerCase().includes(debouncedSearch.toLowerCase()) || c.phone.includes(debouncedSearch);
    const matchTier = !tierFilter || c.tier === tierFilter;
    return matchSearch && matchTier;
  }), [customers, debouncedSearch, tierFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize);

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const openAdd = useCallback(() => {
    setFormData(emptyForm);
    initialFormRef.current = emptyForm;
    setFormErrors({});
    setSubmitError(null);
    setActiveCustomerId(null);
    setModalMode('add');
  }, []);
  const openEdit = useCallback((c: CustomerDisplay) => {
    // Bug #82: nạp đúng trạng thái marketing consent hiện tại (trước đây hard-reset false).
    // Bug #95: `notes` cũng vậy — trước đây hard-reset chuỗi rỗng nên ghi chú đã
    // lưu không bao giờ hiện lại.
    const next: CustomerFormData = {
      fullName: c.fullName, phone: c.phone, email: c.email,
      marketingConsent: c.marketingConsent, notes: c.notes,
    };
    setFormData(next);
    // Bug #104: chụp lại nguyên trạng lúc mở modal để lúc lưu chỉ gửi phần ĐÃ
    // SỬA. Mở khách A rồi mở khách B thì snapshot bị ghi đè ở đây, không có
    // đường nào để giá trị của A rò sang payload của B.
    initialFormRef.current = next;
    setFormErrors({});
    setSubmitError(null);
    setActiveCustomerId(c.id);
    setModalMode('edit');
  }, []);
  const closeModal = useCallback(() => {
    setModalMode(null);
    setActiveCustomerId(null);
    setSubmitError(null);
    setFormErrors({});
    // Bug #104: dọn sạch cả form lẫn snapshot khi đóng — không để lần mở sau
    // nhìn thấy dữ liệu của khách trước dù chỉ trong một khung hình.
    setFormData(emptyForm);
    initialFormRef.current = emptyForm;
  }, []);
  const handleField = (key: keyof CustomerFormData, value: string) => setFormData((p) => ({ ...p, [key]: value }));

  const handleSave = useCallback(async () => {
    const errors: CustomerFormErrors = {};
    if (!formData.fullName.trim()) errors.fullName = t('validationCustomerNameRequired');
    // Bug #104: SĐT bắt buộc khi TẠO khách mới, nhưng khi sửa thì không được
    // dựng rào ở đây. `Customer.phone` là optional cả trong shared-types lẫn
    // schema, và trong dữ liệu thật có khách vãng lai không có số. Bắt buộc ở
    // màn edit nghĩa là những khách đó vĩnh viễn không sửa được marketing
    // consent — đúng triệu chứng của bug này.
    if (modalMode === 'add' && !formData.phone.trim()) errors.phone = t('validationPhoneRequired');
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t('validationInvalidEmailFormat');
    }
    // Ràng buộc DB: xoá trắng CẢ HAI ô liên lạc thì bản ghi không hợp lệ.
    //
    // Rule này THAY cho rào cũ "đã có số mà xoá trắng thì chặn" ở màn edit:
    // rào đó chặn nhầm cả trường hợp hợp lệ — khách có sẵn email, gỡ số điện
    // thoại đi thì vẫn còn đường liên lạc, BE nhận bình thường.
    //
    // Ở mode add rule vẫn được xét, chỉ là không bao giờ nói lên tiếng: phone
    // đang bắt buộc nên hai ô cùng rỗng đã bị `errors.phone` bắt trước. Không
    // hiện chồng hai câu lỗi cho cùng một chỗ trống.
    if (!hasContactInfo(formData) && !errors.phone) {
      errors.contact = t('validationPhoneOrEmailRequired');
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitError(null);
    try {
      if (modalMode === 'add') {
        await createCustomer.mutateAsync({
          full_name: formData.fullName.trim(),
          phone: formData.phone.trim() || undefined,
          email: formData.email.trim() || undefined,
          // Bug #82: trước đây checkbox có trong form nhưng KHÔNG gửi lên server
          // nên consent luôn là "No".
          marketing_consent: formData.marketingConsent,
        });
      } else if (modalMode === 'edit' && activeCustomerId) {
        const before = initialFormRef.current;
        // Bug #104: PATCH mang ĐÚNG những field người dùng vừa sửa.
        //
        // Bật một checkbox thì gửi một field. Field nào không đụng tới thì
        // server giữ nguyên — khách chưa có SĐT/email hay chưa có cấp bậc cũng
        // không còn bị đòi khai lại dữ liệu mình không có.
        //
        // Cấp bậc (tier) KHÔNG có trong payload và không được thêm vào: nó nằm
        // ở loyalty account, không phải thuộc tính của Customer.
        const patch: Partial<UpdateCustomerRequest> & { notes?: string } = {};

        if (formData.fullName.trim() !== before.fullName.trim()) {
          patch.full_name = formData.fullName.trim();
        }
        if (formData.phone.trim() !== before.phone.trim()) {
          patch.phone = formData.phone.trim();
        }
        if (formData.email.trim() !== before.email.trim()) {
          // BE đã chuẩn hoá: `''` và `null` đều được ghi thành NULL. Nên không
          // còn lý do để email đi một đường (null) còn phone đi một đường
          // (chuỗi rỗng) — cùng là "xoá trắng ô" thì cùng gửi chuỗi đã trim.
          // Bỏ luôn cast `as unknown as string` chỉ tồn tại để nhét null vào.
          patch.email = formData.email.trim();
        }
        if (formData.marketingConsent !== before.marketingConsent) {
          patch.marketing_consent = formData.marketingConsent;
        }
        if (formData.notes.trim() !== before.notes.trim()) {
          // Bug #95: `notes` chưa từng có trong payload — ô nhập tồn tại trên
          // giao diện nhưng nội dung không bao giờ rời khỏi trình duyệt.
          //
          // CHẶN Ở BE: model Customer hiện KHÔNG có cột notes (đối chiếu
          // schema + shared-types đều không có). FE đã gửi đúng phần mình;
          // tới khi BE thêm field, DRF vẫn drop im lặng — QA phải test lại
          // SAU khi BE xác nhận.
          //
          // Bug #104: chỉ gửi khi người dùng thật sự sửa ghi chú. Gửi kèm mọi
          // lần lưu thì một field lạ đủ để serializer strict từ chối cả
          // request, kéo theo cả thao tác marketing consent chẳng liên quan.
          patch.notes = formData.notes.trim();
        }

        // Không sửa gì thì không có việc gì để gửi.
        if (Object.keys(patch).length > 0) {
          await updateCustomer.mutateAsync({
            id: activeCustomerId,
            // Cast hẹp thay cho `as never` (reviewer bắt): `as never` tắt kiểm
            // tra kiểu cho TOÀN BỘ object; kiểu giao chỉ nới đúng field `notes`,
            // các field còn lại vẫn được TS đối chiếu với contract.
            data: patch as Partial<UpdateCustomerRequest>,
          });
        }
      }
      closeModal();
    } catch (err) {
      setSubmitError(extractApiErrorMessage(err, t));
    }
  }, [formData, modalMode, activeCustomerId, createCustomer, updateCustomer, closeModal, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmId) return;
    setDeleteError(null);
    try {
      await deleteCustomer.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch (err) {
      // GIỮ hộp thoại mở: thông điệp phải nằm cạnh cái nút vừa bấm thì người ta
      // mới đọc. Đóng hộp thoại rồi bắn toast là cách chắc chắn để không ai thấy.
      setDeleteError(extractApiErrorMessage(err, t));
    }
  }, [deleteConfirmId, deleteCustomer, t]);

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmId(null);
    setDeleteError(null);
  }, []);

  const isSubmitting = createCustomer.isPending || updateCustomer.isPending;

  const { triggerExport, isExporting, progress: exportProgress } = useExport({ endpoint: '/customers/', filePrefix: 'customers' });

  const inputCls = "h-10 w-full rounded-lg border border-slate-300 px-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none";

  return (
    <PageContainer>
      <PageHeader
        title={t('customers')}
        subtitle={t('manageCustomerInformationAndHistory')}
        actions={
          <div className="flex items-center gap-2">
            <ExportButton onExport={triggerExport} isLoading={isExporting} progress={exportProgress} />
            <ActionButton variant="primary" icon={<PlusIcon size={16} />} onClick={openAdd}>{t('addCustomer')}</ActionButton>
          </div>
        }
      />

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard label={t('totalCustomers')} value={statsData?.total ?? customersData?.count ?? 0}
          icon={
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E1F5FE]">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M25.125 10.5C24.9419 13.0419 23.0562 15 21 15C18.9437 15 17.055 13.0425 16.875 10.5C16.6875 7.85562 18.5231 6 21 6C23.4769 6 25.3125 7.90375 25.125 10.5Z" stroke="#0288D1" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M21.0002 19C16.9271 19 13.0102 21.0231 12.029 24.9631C11.899 25.4844 12.2259 26 12.7615 26H29.2396C29.7752 26 30.1002 25.4844 29.9721 24.9631C28.9909 20.96 25.074 19 21.0002 19Z" stroke="#0288D1" stroke-miterlimit="10" />
                <path d="M12.5001 11.6213C12.3539 13.6513 10.8301 15.25 9.18761 15.25C7.54511 15.25 6.01886 13.6519 5.87511 11.6213C5.72573 9.50938 7.20886 8 9.18761 8C11.1664 8 12.6495 9.54813 12.5001 11.6213Z" stroke="#0288D1" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M12.8751 19.1258C11.747 18.6089 10.5045 18.4102 9.18762 18.4102C5.93762 18.4102 2.80637 20.0258 2.022 23.1727C1.91887 23.5889 2.18012 24.0008 2.60762 24.0008H9.62512" stroke="#0288D1" stroke-miterlimit="10" stroke-linecap="round" />
              </svg>
            </div>
          }
        />
        <StatCard label={t('newThisMonth') || 'New This Month'} value={statsData?.new_this_month ?? 0}
          icon={
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDF7ED]">
              <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none">
                <path d="M14.0833 22.75H0.75V19.0167C4.21491 17.0862 8.11692 16.0762 12.0833 16.0833C14.3993 16.0833 16.638 16.4207 18.75 17.05M28.75 22.75H16.75M22.75 16.75V28.75M6.75 6.41667C6.75 7.91956 7.34702 9.3609 8.40973 10.4236C9.47243 11.4863 10.9138 12.0833 12.4167 12.0833C13.9196 12.0833 15.3609 11.4863 16.4236 10.4236C17.4863 9.3609 18.0833 7.91956 18.0833 6.41667C18.0833 4.91377 17.4863 3.47243 16.4236 2.40973C15.3609 1.34702 13.9196 0.75 12.4167 0.75C10.9138 0.75 9.47243 1.34702 8.40973 2.40973C7.34702 3.47243 6.75 4.91377 6.75 6.41667Z" stroke="#219653" stroke-width="1.5" />
              </svg>
            </div>
          }
        />
        <StatCard label={t('vipCustomers')} value={statsData?.vip_count ?? 0}
          icon={
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFFCEB]">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                <path d="M2.67334 25.3346H29.34V27.502H2.67334L2.67334 25.3346ZM2.67334 6.66797L9.34001 11.3346L16.0067 2.66797L22.6733 11.3346L29.34 6.66797V22.668H2.67334V6.66797ZM4.66663 11.0747L4.66663 21.002H27.6666L27.6666 11.002L22.1666 14.002L16.0067 5.9173L9.66663 14.002L4.66663 11.0747Z" fill="#F5DD09" />
              </svg>
            </div>
          }
        />
      </div>

      {/* Table card */}
      <div className="mt-6 rounded-xl bg-white shadow-sm" style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.05)' }}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-6 py-4">
          <div className="relative min-w-[200px] max-w-xs flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" /><path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            <input
              type="text"
              placeholder={t('searchCustomer') || 'Search customer...'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              enterKeyHint="search"
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none"
            />
            {searchInput !== debouncedSearch && searchInput && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="0">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="relative">
              <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="h-9 appearance-none rounded-lg border border-slate-200 bg-white pl-3 pr-8 text-sm text-slate-600 focus:outline-none">
                <option value="">{t('allTiers')}</option>
                <option value="ELITE">{t('elite')}</option>
                <option value="PLATINUM">{t('platinum')}</option>
                <option value="GOLD">{t('gold')}</option>
                <option value="SILVER">{t('silver')}</option>
                <option value="BRONZE">{t('bronze')}</option>
                <option value="NONE">{t('none')}</option>
              </select>
              <ChevronIcon />
            </div>
            {/* Bug #83: bỏ filter Status (Active/Inactive) — Customer không có
                field status thật để set, filter chỉ gây mâu thuẫn dữ liệu. */}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                {[
                  t('code'),
                  t('customerName'),
                  t('phone'),
                  t('marketingConsent'),
                  t('tier'),
                  t('action')
                ].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.6px] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-4"><div className="h-4 w-16 animate-pulse rounded bg-slate-200" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">{t('noCustomersFound')}</td></tr>
              ) : (
                paginatedRows.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-700">{c.code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 font-semibold text-slate-900">
                        {c.fullName}
                        {c.isVip && <span className="text-yellow-400 text-xs">⭐</span>}
                      </div>
                      {c.email && <div className="text-xs text-slate-400">{c.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{c.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.marketingConsent ? 'bg-[#DCFCE7] text-[#15803D]' : 'bg-slate-100 text-slate-500'}`}>
                        {c.marketingConsent ? t('yes') || 'Yes' : t('no') || 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3"><TierBadge tier={c.tier} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {/* Nút Liên kết/Gỡ liên kết tài khoản đã BỎ (23/09/2026).
                            Hệ thống tự nhận hồ sơ khi tài khoản khách xác minh
                            xong, nên quy trình bình thường không còn thao tác
                            nối tay nào. Ba ca hệ thống cố tình không tự quyết
                            (tài khoản chưa xác minh, hai hồ sơ trùng một SĐT,
                            SĐT trùng hồ sơ của tài khoản khác) do BE ops xử lý
                            qua Django admin — endpoint link-user/unlink-user
                            phía BE vẫn còn. Đừng dựng lại nút ở đây: nó từng
                            nằm trên MỌI dòng mà không có vấn đề gì để giải
                            quyết, và không ai hiểu nó để làm gì. */}
                        <button onClick={() => openEdit(c)} className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"><EditIcon size={15} /></button>
                        <button onClick={() => setDeleteConfirmId(c.id)} className="flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"><TrashIcon size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={pageSize}
            onPageChange={(page) => {
              setCurrentPage(page);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
              });
            }}
            itemLabel="customers"
          />
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={closeModal}>
          <div className="w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-bold text-slate-900">{modalMode === 'add' ? t('addCustomer') : t('editCustomer')}</h3>
              <button onClick={closeModal} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><XIcon /></button>
            </div>
            <div className="space-y-4 px-6 pb-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('customerNameLabel')} <span className="text-red-500">*</span></label>
                  <input value={formData.fullName} onChange={(e) => handleField('fullName', e.target.value)} placeholder={t('enterCustomerNamePlaceholder')} className={`${inputCls} ${formErrors.fullName ? 'border-red-300 bg-red-50' : ''}`} />
                  {formErrors.fullName && <p className="mt-1 text-xs text-red-600">{formErrors.fullName}</p>}
                </div>
                <div>
                  {/* Dấu * bám đúng rule đang chạy, không nói một đằng validate
                      một nẻo: mode add thì phone luôn bắt buộc; mode edit thì
                      phone chỉ bắt buộc khi email đang trống (và ngược lại) —
                      vì khách phải còn ít nhất một đường liên lạc. */}
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    {t('phone')}
                    {(modalMode === 'add' || !formData.email.trim()) && <span className="text-red-500"> *</span>}
                  </label>
                  <input value={formData.phone} onChange={(e) => handleField('phone', e.target.value)} placeholder={t('enterPhonePlaceholder')} className={`${inputCls} ${formErrors.phone || formErrors.contact ? 'border-red-300 bg-red-50' : ''}`} />
                  {formErrors.phone && <p className="mt-1 text-xs text-red-600">{formErrors.phone}</p>}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  {t('email')}
                  {modalMode === 'edit' && !formData.phone.trim() && <span className="text-red-500"> *</span>}
                </label>
                <input value={formData.email} onChange={(e) => handleField('email', e.target.value)} placeholder={t('enterEmailPlaceholder')} className={`${inputCls} ${formErrors.email || formErrors.contact ? 'border-red-300 bg-red-50' : ''}`} />
                {formErrors.email && <p className="mt-1 text-xs text-red-600">{formErrors.email}</p>}
                {/* Lỗi cấp form: đặt ngay dưới CẢ HAI ô nó nói về, và tô viền
                    đỏ cả hai — treo dưới riêng một ô thì đọc thành lỗi của ô đó. */}
                {formErrors.contact && <p className="mt-1 text-xs text-red-600">{formErrors.contact}</p>}
              </div>
              {/* Bug #82: checkbox consent hiển thị ở CẢ add lẫn edit để sửa được trạng thái */}
              <div className="flex items-center gap-2">
                <input id="marketing_consent" type="checkbox" checked={formData.marketingConsent} onChange={(e) => setFormData((p) => ({ ...p, marketingConsent: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400" />
                <label htmlFor="marketing_consent" className="text-sm text-slate-700">{t('marketingConsentLabel')}</label>
              </div>
              {modalMode === 'edit' && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('notes') || 'Notes'}</label>
                  <textarea value={formData.notes} onChange={(e) => handleField('notes', e.target.value)} placeholder={t('enterNotesPlaceholder')} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm placeholder-slate-400 focus:border-blue-400 focus:outline-none resize-none" />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
              {/* Bug #104: lỗi từ server có thể là nhiều dòng (mỗi field một dòng). */}
              {submitError && <p className="mr-auto whitespace-pre-line text-xs text-red-600">{submitError}</p>}
              <button onClick={closeModal} className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">{t('cancel')}</button>
              <button onClick={handleSave} disabled={isSubmitting} className="rounded-lg bg-[#0288D1] px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {isSubmitting ? t('savingBtn') : t('saveBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={closeDeleteConfirm}>
          <div className="w-full max-w-[440px] rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4">
              <h3 className="text-base font-bold text-slate-900">{t('deleteCustomer')}</h3>
              <button onClick={closeDeleteConfirm} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors"><XIcon /></button>
            </div>
            <div className="border-t border-slate-100" />
            <div className="px-6 py-5">
              <p className="text-sm text-slate-500">{t('areYouSureDeleteCustomer')}</p>
              {/* Câu của BE, hiển thị nguyên văn — nó nói rõ phải gỡ liên kết
                  hoặc vô hiệu hoá tài khoản trước, chi tiết hơn bất cứ câu
                  chung nào client tự nghĩ ra. */}
              {deleteError ? (
                <p className="mt-3 whitespace-pre-line rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {deleteError}
                </p>
              ) : null}
            </div>
            <div className="border-t border-slate-100" />
            <div className="flex items-center justify-end gap-3 px-6 py-4">
              <button onClick={closeDeleteConfirm} className="px-5 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors">{t('cancel')}</button>
              <button onClick={handleConfirmDelete} disabled={deleteCustomer.isPending} className="rounded-lg bg-[#F44336] px-5 py-2 text-sm font-semibold text-white hover:bg-red-600 disabled:opacity-60 transition-colors">
                {deleteCustomer.isPending ? t('deleting') || 'Deleting...' : t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}

    </PageContainer>
  );
}
